// ============================================================================
// ClientResetPage — route /client/reset?token= (PUBLIC, client realm). The
// client follows the emailed reset link, lands here with ?token=, and sets a new
// password (POST /api/client/reset via useClientAuth().reset). The server
// validates the token (single-use / unexpired / subject-bound → 400 otherwise),
// writes the new argon2 hash to that client's row only, consumes the token, and
// signs the client straight in (vf_client_session cookie) — so on success we
// drop them into their portal home. New password must be >= 8 chars (client
// rule), validated client-side BEFORE the request so a too-short attempt never
// burns the token.
//
// Mobile-first Pulse, EN + 繁中 via i18n keys only. Reuses ClientAuthBrand.
// ============================================================================
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner, Button, Card, TextField } from "../../components";
import { ApiError } from "../../api";
import { useClientAuth } from "../auth/ClientAuthContext";
import { ClientLanguageToggle } from "../ClientLanguageToggle";
import { ClientAuthBrand } from "./ClientAuthBrand";
import { CLIENT_HOME_PATH, CLIENT_LOGIN_PATH } from "../routes";

const MIN_PASSWORD = 8; // client rule (matches the backend reset endpoint).

export function ClientResetPage() {
  const { t } = useTranslation("clientPortal");
  const { reset, isAuthenticated, isLoading } = useClientAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // An already-signed-in client has no business on the reset screen.
  if (!isLoading && isAuthenticated && !submitting)
    return <Navigate to={CLIENT_HOME_PATH} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError(t("reset.noToken"));
      return;
    }
    // Validate length client-side BEFORE the request so a too-short attempt
    // never consumes the single-use token.
    if (password.length < MIN_PASSWORD) {
      setError(t("reset.tooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await reset({ token, new_password: password });
      navigate(CLIENT_HOME_PATH, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("reset.invalidToken"));
      } else if (err instanceof ApiError && err.status === 422) {
        setError(t("reset.tooShort"));
      } else {
        setError(t("reset.genericError"));
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-body">
      <header className="mx-auto flex max-w-md items-center justify-between px-5 py-5">
        <ClientAuthBrand />
        <ClientLanguageToggle />
      </header>
      <main className="mx-auto flex max-w-md flex-col px-5 pb-16 pt-2">
        <Card className="space-y-5">
          <div>
            <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
              {t("reset.kicker")}
            </p>
            <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
              {t("reset.title")}
            </h1>
            <p className="mt-1.5 text-body-soft">{t("reset.blurb")}</p>
          </div>

          {!token && <Banner tone="warning" title={t("reset.noToken")} />}
          {error && <Banner tone="danger" title={error} />}

          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <TextField
              label={t("reset.newPassword")}
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              hint={t("reset.hint")}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              required
              disabled={submitting || !token}
            />
            <TextField
              label={t("reset.confirm")}
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                setError(null);
              }}
              required
              disabled={submitting || !token}
            />
            <label className="flex items-center gap-2 text-sm text-body-soft">
              <input
                type="checkbox"
                checked={show}
                onChange={(e) => setShow(e.target.checked)}
                className="h-4 w-4 rounded border-line text-coral focus-visible:ring-volt"
              />
              {t("reset.showPassword")}
            </label>
            <Button type="submit" block size="lg" disabled={submitting || !token}>
              {submitting ? t("reset.saving") : t("reset.submit")}
            </Button>
          </form>

          <p className="text-center text-sm text-body-soft">
            {t("reset.remembered")}{" "}
            <Link
              to={CLIENT_LOGIN_PATH}
              className="font-bold text-coral hover:underline"
            >
              {t("reset.backToLogin")}
            </Link>
          </p>
        </Card>
      </main>
    </div>
  );
}
